import React, { useState } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { ReactComponent as StarIcon } from "feather-icons/dist/icons/star.svg";
import { ReactComponent as TimeIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import HardDiskImage from "../../images/hard-disk.jpeg";
import AppleMailPhoneImage from "../../images/apple-mail-phone.jpeg";
import PointingToLaptopImage from "../../images/pointing-to-laptop.jpeg";
import GitCommitHistoryImage from "../../images/git-comit-history.jpeg";


const Container = tw.div`relative bg-gray-900 text-gray-100 -mx-8 -mt-8`;
const Content = tw.div`max-w-screen-xl mx-auto py-16 lg:py-20`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-16`}
  .slick-track { 
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;
const Card = tw.div`h-full flex! flex-col sm:border sm:border-gray-800 sm:rounded-3xl sm:overflow-hidden max-w-sm sm:rounded-tl-4xl sm:rounded-br-5xl relative focus:outline-none`;
const CardImage = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`w-full h-56 sm:h-64 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`
]);

const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const Title = tw.h5`text-2xl font-bold`;

const RatingsInfo = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500 fill-current`}
  }
`;
const Rating = tw.span`ml-2 font-bold`;

const Description = tw.p`text-sm leading-loose mt-2 sm:mt-4`;

const SecondaryInfoContainer = tw.div`flex flex-col sm:flex-row mt-2 sm:mt-4`;
const IconWithText = tw.div`flex items-center mr-6 my-2 sm:my-0`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full p-2 bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;
const Text = tw.div`ml-2 text-sm font-semibold text-gray-800`;

const PrimaryButton = tw(PrimaryButtonBase)`mt-auto sm:text-lg rounded-none w-full rounded sm:rounded-none sm:rounded-br-4xl py-3 sm:py-6`;
export default () => {
  // useState is used instead of useRef below because we want to re-render when sliderRef becomes available (not null)
  const [sliderRef, setSliderRef] = useState(null);
  const sliderSettings = {
    arrows: false,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },

      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      },
    ]
  };

    /* Change this according to your needs */
    // http://unsplash.com/photos/842ofHC6MaI/download -> To download the images that are old and not available by unsplash
    const cards = [
        {
            imageSrc: HardDiskImage,
            title: "Ceph Setup",
            description: "Configure Ceph Storage Cluster and deploy it on your infrastructure. Scale your storage needs with Ceph. Multi-tenancy and high availability.",
            timeText: "1 - 2 Weeks",
            rating: "4.1",
            pricingUrl: "https://calendly.com/neko-nik/general-meet",
            pricingText: "Schedule a Virtual Meet",
        },
        {
            imageSrc: AppleMailPhoneImage,
            title: "Email Services",
            description: "Storage based email services for your organization. Secure and reliable for your business needs. Unlimited Users and domains. For affordable prices.",
            timeText: "1 - 3 Days",
            rating: "4.6",
            pricingUrl: "https://calendly.com/neko-nik/general-meet",
            pricingText: "Schedule a Virtual Meet",
        },
        {
            imageSrc: PointingToLaptopImage,
            title: "Custom Solutions",
            description: "Custom solutions for your business needs. We provide end to end solutions for your business. Contact us for more information.",
            timeText: "Varies",
            rating: "4.5",
            pricingUrl: "https://calendly.com/neko-nik/general-meet",
            pricingText: "Schedule a Virtual Meet",
        },
        {
            imageSrc: GitCommitHistoryImage,
            title: "Self Hosted Git",
            description: "Self hosted Gitlab or Gitea setup. Manage your code and repositories on your own infrastructure. Secure and reliable. CI/CD pipelines.",
            timeText: "1 - 2 Weeks",
            rating: "4.7",
            pricingUrl: "https://calendly.com/neko-nik/general-meet",
            pricingText: "Schedule a Virtual Meet",
        },
    ]

  return (
    <Container>
      <Content>
        <HeadingWithControl>
          <Heading>Featured Solutions</Heading>
          <Controls>
            <PrevButton onClick={sliderRef?.slickPrev}><ChevronLeftIcon/></PrevButton>
            <NextButton onClick={sliderRef?.slickNext}><ChevronRightIcon/></NextButton>
          </Controls>
        </HeadingWithControl>
        <CardSlider ref={setSliderRef} {...sliderSettings}>
          {cards.map((card, index) => (
            <Card key={index}>
              <CardImage imageSrc={card.imageSrc} />
              <TextInfo>
                <TitleReviewContainer>
                  <Title>{card.title}</Title>
                  <RatingsInfo>
                    <StarIcon />
                    <Rating>{card.rating}</Rating>
                  </RatingsInfo>
                </TitleReviewContainer>
                <SecondaryInfoContainer>
                  <IconWithText>
                    <IconContainer>
                      <TimeIcon />
                    </IconContainer>
                    <Text>{card.timeText}</Text>
                  </IconWithText>
                </SecondaryInfoContainer>
                <Description>{card.description}</Description>
              </TextInfo>
              <PrimaryButton as="a" href={card.pricingUrl}>{card.pricingText}</PrimaryButton>
            </Card>
          ))}
        </CardSlider>
      </Content>
    </Container>
  );
};
